import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import clsx from 'clsx';
import { FiChevronDown, FiGlobe } from 'react-icons/fi';
import logo from 'assets/header/smileLogo.svg';
import darkLogo from 'assets/header/darkSmileLogo.svg';
import SpecialistCta from '../SpecialistCta';
import scrollToTop from 'utils/scrollToTop';
import isActiveLink, { activeSectionKey, rememberSection } from '../isActiveLink';
import css from './css.module.scss';
import { SmartLogin } from '../desktopMenu/presentational';
import DetermineNavigation from '../DetermineNavigation';
import { icons } from '../desktopMenu/MegaMenu';

const MobileMenu = ({
  changeLanguage,
  i18n,
  language,
  languagesList,
  navItems,
  sendDemoEvent,
  setShowMobileMenu,
  showMobileMenu,
  advertisingView,
  version2,
  onMenuOpen,
}) => {
  const [openGroup, setOpenGroup] = useState(null);
  const location = useLocation();
  const currentSection = activeSectionKey(navItems, location.pathname);
  const close = () => setShowMobileMenu(false);

  // lock page scroll while the menu covers the screen
  useEffect(() => {
    document.body.classList.toggle(css.noScroll, showMobileMenu);
    if (!showMobileMenu) setOpenGroup(null);
    return () => document.body.classList.remove(css.noScroll);
  }, [showMobileMenu]);

  return (
    <div className={clsx(css.container, showMobileMenu && css.open, (advertisingView && version2) && css['dark-container'])}>
      <header>
        <Link to={advertisingView ? '#' : `/${language}/`} onClick={() => { close(); scrollToTop(); }}>
          <img width="100%" height="100%" loading="lazy" className={css.logo} src={(advertisingView && version2) ? darkLogo : logo} alt="smile logo" />
        </Link>
        <div className={css.headerRight}>
          {!showMobileMenu && (
            <div className={css.cta}>
              <SpecialistCta language={language} advertisingView={advertisingView} sendDemoEvent={sendDemoEvent} />
            </div>
          )}
          <button
            type="button"
            className={clsx(css.burger, showMobileMenu && css.burgerOpen)}
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            aria-label="menu"
            aria-expanded={showMobileMenu}
          >
            <span />
            <span />
          </button>
        </div>
      </header>

      <div className={css.panel} aria-hidden={!showMobileMenu}>
        <ul className={css.items}>
          {navItems.map((item) => (
            <li key={item.key || item.path} className={css.item}>
              {item.menu ? (
                <>
                  {/* the name opens the section's page; the arrow expands its sub-sections */}
                  <div
                    className={clsx(
                      css.groupTrigger,
                      openGroup === item.key && css.groupOpen,
                      item.key === currentSection && css.groupCurrent,
                    )}
                  >
                    <Link
                      to={item.path}
                      className={css.groupLink}
                      onClick={() => {
                        rememberSection(item.key, item.path);
                        close();
                      }}
                    >
                      {item.title}
                    </Link>
                    <button
                      type="button"
                      className={css.groupToggle}
                      aria-expanded={openGroup === item.key}
                      aria-label={item.title}
                      onClick={() => {
                        if (openGroup !== item.key) onMenuOpen(item.key);
                        setOpenGroup(openGroup === item.key ? null : item.key);
                      }}
                    >
                      <FiChevronDown className={css.chevron} />
                    </button>
                  </div>
                  <div className={clsx(css.group, openGroup === item.key && css.groupVisible)}>
                    <div className={css.groupInner}>
                      {item.menu.map((column) => column.links.map((link) => {
                        if (link.skeleton) {
                          return (
                            <span key={`${column.title}-${link.key}`} className={css.subSkeleton} aria-hidden>
                              <span className={css.subSkeletonIcon} />
                              <span className={css.subSkeletonText} />
                            </span>
                          );
                        }
                        const Icon = icons[link.icon];
                        return (
                          <HashLink smooth key={`${column.title}-${link.key}`} to={link.path} className={clsx(css.subLink, !link.desc && css.subLinkSingle, item.key === currentSection && !link.noActive && isActiveLink(link.path, location) && css.subLinkActive)} onClick={() => { rememberSection(item.key, link.path); close(); }}>
                            <span className={css.subIcon}>{Icon && <Icon />}</span>
                            <span>
                              <span className={css.subTitle}>{link.title}</span>
                              {link.desc && <span className={css.subDesc}>{link.desc}</span>}
                            </span>
                          </HashLink>
                        );
                      }))}
                    </div>
                  </div>
                </>
              ) : (
                <span onClick={close}>
                  <DetermineNavigation item={item} css={css} advertisingView={advertisingView} />
                </span>
              )}
            </li>
          ))}
        </ul>

        <div className={css.footer}>
          {!advertisingView && (
            <div className={css.languages}>
              <FiGlobe />
              {languagesList.map((lang) => (
                <button
                  key={lang}
                  type="button"
                  className={clsx(css.langOption, i18n.language === lang && css.langActive)}
                  onClick={() => changeLanguage(lang)}
                >
                  {lang}
                </button>
              ))}
            </div>
          )}
          {!advertisingView && <div className={css.signIn}><SmartLogin /></div>}
          <div className={clsx(css.cta, css.ctaFull)} onClick={close}>
            <SpecialistCta language={language} advertisingView={advertisingView} sendDemoEvent={sendDemoEvent} />
          </div>
        </div>
      </div>
    </div>
  );
};

MobileMenu.defaultProps = {
  onMenuOpen: () => {},
  showMobileMenu: false,
  setShowMobileMenu: () => { },
  changeLanguage: () => { },
};

MobileMenu.propTypes = {
  onMenuOpen: PropTypes.func,
  showMobileMenu: PropTypes.bool,
  setShowMobileMenu: PropTypes.func,
  navItems: PropTypes.array.isRequired,
  i18n: PropTypes.any.isRequired,
  languagesList: PropTypes.array.isRequired,
  changeLanguage: PropTypes.func,
};
export default MobileMenu;
